import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageAvailable } from '@shared/services/language/models';
import { LANGUAGES } from '@shared/services/language/registry';
import { LogsService } from '@shared/services/logs';
import { CommonStore, LocalizedCommonStore } from '@stores';
import { map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly logs = inject(LogsService);
  private readonly localizedCommon = inject(LocalizedCommonStore).read;
  private readonly commonStore = inject(CommonStore);

  /**
   * Switches the translations and the common data to the given language.
   *
   * Attempts to change the current language via `TranslateService.use(...)`. If the requested language
   * is already active, returns an observable that synchronously emits the current language and completes.
   * Also emits an informational log entry. The current language itself comes from {@link CurrentLanguageService}.
   *
   * @param language The BCP 47 locale to activate (for example, `'fr-FR'`). Should be one of the supported languages.
   * @returns Observable that emits the activated language once the underlying translation resources are loaded.
   * @example
   * languageService.use('fr-FR').subscribe((lang) => {
   *   console.log('Language set to', lang);
   * });
   * @see TranslateService#use
   */
  use(language: LanguageAvailable): Observable<LanguageAvailable> {
    const current = this.translateService.getCurrentLang() as LanguageAvailable;
    if (current === language) {
      return of(current);
    }
    const { langcode } = LANGUAGES[language];
    const localizedCommon = this.localizedCommon();
    const commonByLanguage = localizedCommon?.[langcode];
    if (commonByLanguage) {
      this.commonStore.set(commonByLanguage);
    } else if (localizedCommon) {
      this.logs.error('LanguageService', `No common data for langcode "${langcode}"`, {
        langcodes: Object.keys(localizedCommon),
      });
    }
    this.logs.info('LanguageService', `Changing language to "${language}"`);
    return this.translateService.use(language).pipe(map(() => language));
  }

  /**
   * Performs a synchronous translation lookup for the provided key(s).
   *
   * Delegates to `TranslateService.instant(...)` to immediately resolve a translation string or
   * object without waiting for asynchronous resource loading. Prefer this for imperative code paths
   * where translations are already available; in templates, prefer the translate pipe.
   *
   * @param key A translation key or an array of keys to resolve.
   * @param interpolateParams Optional parameters used to interpolate placeholders within the translation value.
   * @returns The resolved translation value(s). When `key` is a string, returns a single value; when `key` is an array, returns a map of key to value.
   * @example
   * const title = languageService.instant('home.title');
   * const withParams = languageService.instant('greeting', { name: 'Socooc' });
   * @see TranslateService#instant
   */
  instant(key: string | string[], interpolateParams?: Record<string, unknown>): string {
    return this.translateService.instant(key, interpolateParams) as string;
  }
}
